import type { EnvironmentId } from "@cmmd-forge/contracts";

import { readEnvironmentBearerToken } from "../environments/runtime/cloudRuntimeCredentialAccess";
import { reconnectSavedEnvironment } from "../environments/runtime/service";
import { getSavedEnvironmentRecord } from "../environments/runtime/catalog";
import * as savedCloud from "../environments/runtime/savedCloudEnvironmentLifecycle";
import {
  ensureEnvironmentAttachmentUploadTarget,
  resolveEnvironmentHttpUrl,
} from "../environments/runtime/catalog";

export async function downloadEnvironmentAttachment(input: {
  readonly environmentId: EnvironmentId;
  readonly attachmentId: string;
  readonly filename: string;
  readonly fetchImpl?: typeof fetch;
}): Promise<void> {
  let bearerToken = await readEnvironmentBearerToken(input.environmentId);
  const record = getSavedEnvironmentRecord(input.environmentId);
  if (!bearerToken && record && savedCloud.isCmmdCloudEnvironment(record)) {
    await reconnectSavedEnvironment(input.environmentId);
    bearerToken = await readEnvironmentBearerToken(input.environmentId);
    if (!bearerToken)
      throw new Error("Cloud runtime connection did not provide an attachment credential.");
  }
  ensureEnvironmentAttachmentUploadTarget(input.environmentId);
  const url = resolveEnvironmentHttpUrl({
    environmentId: input.environmentId,
    pathname: `/attachments/${encodeURIComponent(input.attachmentId)}`,
  });
  const response = await (input.fetchImpl ?? fetch)(url, {
    headers: bearerToken ? { Authorization: `Bearer ${bearerToken}` } : {},
  });
  if (!response.ok) {
    throw new Error(`Attachment download failed with status ${response.status}.`);
  }
  const blobUrl = URL.createObjectURL(await response.blob());
  try {
    const anchor = document.createElement("a");
    anchor.href = blobUrl;
    anchor.download = input.filename;
    anchor.click();
  } finally {
    URL.revokeObjectURL(blobUrl);
  }
}
